import { Box, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { Fragment, useState } from "react";
import { getTranslation } from "../../utils/translate";
import useSWR from "swr";
import { fetcher } from "../../common/utils/fetcher";
import KeyValueCard from "../KeyValueCard";
import BarChart from "../shared/BarChart";
import { chooseInputComponent } from "../../common/utils/components";
import TableDisplay from "../TableDisplay";
import DataVisualiser from "../DataVisualiser";
import { InsightsChartSkeleton } from "./InsightsAsyncState";
import { insightsSectionClassName } from "../../common/utils/insightsDesignEnabled";

const RecipesInsights = () => {
    const retailer = useSelector((state) => state.retailer);
    const [contestId, setContestId] = useState('cd992816-30d5-40c9-8092-0755c8eff0cd');
    const [recipeId, setRecipeId] = useState('ab9f2eee-f9e5-4c65-9564-bcd0fcd0e9ce');

    const { data: contestData } = useSWR({
        url: 'contests',
        fields: 'id,achievements.*',
        filter: {
            _and: [
                {
                    id: {
                        _eq: contestId,
                    }
                },
            ],
        },
    }, fetcher);

    let contest = null;

    if (contestData) {
        contest = contestData[0];
    }

    const { data: thisYearRecipesPageViewsData, isLoading: thisYearRecipesPageViewsDataLoading } = useSWR({
        url: 'page_views',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    url: {
                        _contains: '/recipe/',
                    }
                },
                {
                    retailer: {
                        _eq: retailer.id,
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['url'],
        limit: 100,
        sort: ['-count'],
    }, fetcher);

    let thisYearRecipesPageViews = null;

    if (thisYearRecipesPageViewsData) {
        thisYearRecipesPageViews = thisYearRecipesPageViewsData;

        thisYearRecipesPageViews = thisYearRecipesPageViews.map((item) => {
            let id;
            
            // Extract recipe ID from URL path (e.g., /recipe/abc123 -> abc123)
            const pathWithoutQuery = item.url.split('?')[0];
            id = pathWithoutQuery
                .replace('/recipe/', '')
                .split('/')[0];

            return {
                ...item,
                id: id,
            };
        });
    }

    // Extract unique IDs for fetching recipe titles
    const recipeIds = thisYearRecipesPageViews 
        ? [...new Set(thisYearRecipesPageViews.map(item => item.id).filter(id => id && id !== 'undefined' && id !== 'null'))]
        : [];

    // Fetch recipe titles for the extracted IDs
    const { data: recipesData, isLoading: recipesDataLoading } = useSWR(
        recipeIds.length > 0 ? {
            url: 'recipe',
            fields: 'id,layout_translations.*',
            filter: {
                id: {
                    _in: recipeIds
                }
            },
            limit: -1
        } : null,
        fetcher
    );

    // Create a mapping of ID to title
    const recipeTitlesMap = {};
    if (recipesData) {
        recipesData.forEach(recipe => {
            const translatedRecipe = getTranslation(recipe, { key: 'languages_code', code: 'en-US' }, 'layout_translations');
            recipeTitlesMap[recipe.id] = translatedRecipe?.name || 'Unknown Recipe';
        });
    }

    // Update the data with titles and combine entries with the same ID
    if (thisYearRecipesPageViews && recipesData && Object.keys(recipeTitlesMap).length > 0) {
        // First, add titles to all items
        const itemsWithTitles = thisYearRecipesPageViews.map((item) => ({
            ...item,
            title: recipeTitlesMap[item.id] || `Recipe ${item.id}`,
        }));

        // Group by ID and sum the counts
        const groupedData = itemsWithTitles.reduce((acc, item) => {
            const existingItem = acc.find(groupedItem => groupedItem.id === item.id);
            
            if (existingItem) {
                // Sum the counts for the same ID
                existingItem.count += item.count;
            } else {
                // Add new item
                acc.push({ ...item });
            }
            
            return acc;
        }, []);

        thisYearRecipesPageViews = groupedData;
    }

    console.log('thisYearRecipesPageViews: ', thisYearRecipesPageViews);
    console.log('recipeTitlesMap: ', recipeTitlesMap);
    console.log('recipeIds: ', recipeIds);


    const { data: thisYearRecipesRetailersPageViewsData } = useSWR({
        url: 'page_views',
        filter: {
            _and: [
                {
                    date_created: {
                        _gte: '2025-01-01',
                    }
                },
                {
                    url: {
                        _contains: '/recipe/',
                    }
                },
            ],
        },
        aggregate: {
            count: '*',
        },
        groupBy: ['retailer'],
        limit: -1,
        sort: ['-count'],
    }, fetcher);

    let thisYearRecipesRetailersPageViews = null;

    if (thisYearRecipesRetailersPageViewsData) {
        thisYearRecipesRetailersPageViews = thisYearRecipesRetailersPageViewsData;
    }

    // Extract unique retailer IDs for fetching retailer names
    const retailerIds = thisYearRecipesRetailersPageViews 
        ? [...new Set(thisYearRecipesRetailersPageViews.map(item => item.retailer).filter(id => id && id !== 'undefined' && id !== 'null'))]
        : [];

    // Fetch retailer names for the extracted IDs
    const { data: retailersData } = useSWR(
        retailerIds.length > 0 ? {
            url: 'retailer',
            fields: 'id,translations.*',
            filter: {
                id: {
                    _in: retailerIds
                }
            },
            limit: -1
        } : null,
        fetcher
    );

    // Create a mapping of ID to name
    const retailerNamesMap = {};
    if (retailersData) {
        retailersData.forEach(retailer => {
            const translatedRetailer = getTranslation(retailer, { key: 'languages_code', code: 'en-US' });
            retailerNamesMap[retailer.id] = translatedRetailer?.retailer_name || 'Unknown Retailer';
        });
    }

    // Update the data with names
    if (thisYearRecipesRetailersPageViews && retailersData && Object.keys(retailerNamesMap).length > 0) {
        thisYearRecipesRetailersPageViews = thisYearRecipesRetailersPageViews.map((item) => ({
            ...item,
            title: retailerNamesMap[item.retailer] || `Retailer ${item.retailer}`,
        }));
    }

    return (
        <Box className={insightsSectionClassName("insights-section--content")}>
            {
                (thisYearRecipesPageViewsDataLoading || (recipeIds.length > 0 && recipesDataLoading)) ? (
                    <InsightsChartSkeleton />
                ) : (
                    <> 
                        <Box>
                            <DataVisualiser
                                data={thisYearRecipesPageViews}
                                xKey="title"
                                yKey="count"
                                xLabel="Recipe"
                                showXAxisValues={false}
                                yLabel="Page Views"
                                title="Top Recipes"
                                description="The most viewed recipes this year"
                                emptyMessage="No recipe page views this year."
                            />
                        </Box>

                        {/* <Box>
                            <DataVisualiser
                                data={thisYearRecipesRetailersPageViews}
                                xKey="title"
                                yKey="count"
                                xLabel="Retailer"
                                showXAxisValues={false}
                                yLabel="Count"
                                title="Recipes Retailers Page Views"
                                description="Recipes Retailers Page Views"
                            />
                        </Box> */}
                    </>
                )
            }
        </Box >
    )
}

export default RecipesInsights